// src/TaskDetailPanel.js
import React from 'react';
import api, { API_BASE } from './apiClient';
import {
  Box,
  Button,
  Checkbox,
  Drawer,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  ListItem,
  IconButton,
  MenuItem,
  Select,
  Tabs,
  Tab,
  TextField,
  Typography,
  Divider,
  Chip,
  Avatar,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import LockIcon from '@mui/icons-material/Lock';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import FlagIcon from '@mui/icons-material/Flag';
import FolderIcon from '@mui/icons-material/Folder';
import LabelIcon from '@mui/icons-material/Label';
import ProjectMultiTagSelector from './ProjectMultiTagSelector';

// User display helpers for avatars
const getUploadsBase = () => {
  if (API_BASE && API_BASE.startsWith('http')) return API_BASE;
  if (api?.defaults?.baseURL && /^https?:/i.test(api.defaults.baseURL)) return api.defaults.baseURL;
  return window.location.origin;
};

const isImageFile = (name = '') =>
  /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name || '');

const getFileExt = (name = '') => {
  const parts = String(name).split('.');
  if (parts.length < 2) return 'file';
  return parts.pop().toLowerCase() || 'file';
};

const buildUserDisplay = (user = {}) => {
  if (typeof user === 'string') {
    const parts = user.trim().split(/\s+/).filter(Boolean);
    const firstName = parts[0] || user || 'User';
    const lastName = parts.length > 1 ? parts.slice(1).join(' ') : '';
    return buildUserDisplay({ first_name: firstName, last_name: lastName });
  }

  const firstName =
    user.first_name || user.firstname || user.firstName || user.username || user.email || '';
  const lastName = user.last_name || user.lastname || user.lastName || '';
  const username = user.username || user.email || '';
  const fullName = `${(firstName || '').trim()} ${(lastName || '').trim()}`.trim() || username || 'User';

  const words = (firstName || '').trim().split(/\s+/).filter(Boolean);
  let initials = '';
  if (firstName && lastName) {
    initials = `${firstName[0]}${lastName[0]}`;
  } else if (words.length >= 2) {
    initials = `${words[0][0]}${words[1][0]}`;
  } else if (words.length === 1) {
    initials = words[0][0];
  } else if (username) {
    initials = username[0];
  }
  initials = initials.toUpperCase();

  const avatarSrc =
    user.avatar ||
    user.avatar_url ||
    user.profile_pic ||
    user.profilePic ||
    user.photo_url ||
    (user.photo ? `${getUploadsBase()}/uploads/${user.photo}` : null) ||
    null;

  return { firstName: firstName || username || 'User', lastName, fullName, initials, avatarSrc };
};

const avatarPalette = ['#00897B', '#2563EB', '#7C3AED', '#EA580C', '#0EA5E9', '#E11D48', '#0F766E'];
const pickAvatarColor = (seed = '') => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return avatarPalette[hash % avatarPalette.length];
};

const UserAvatar = ({ user, size = 28, showName = false }) => {
  const data = buildUserDisplay(user || {});
  const bg = pickAvatarColor(data.fullName || data.initials || 'u');
  return (
    <Tooltip title={data.fullName} placement="top">
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: showName ? 0.75 : 0 }}>
        <Avatar
          src={data.avatarSrc || undefined}
          sx={{ width: size, height: size, fontSize: size * 0.45, bgcolor: bg }}
        >
          {!data.avatarSrc && data.initials}
        </Avatar>
        {showName && (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {data.firstName}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

const projectPalette = ['#0284c7', '#0ea5e9', '#22c55e', '#f97316', '#e11d48', '#7c3aed', '#0f766e'];
const pickProjectColor = (seed = '') => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 33 + seed.charCodeAt(i)) >>> 0;
  return projectPalette[hash % projectPalette.length];
};

const getProjectInitials = (name = '') => {
  const clean = name.replace(/[^A-Za-z0-9\\s]/g, ' ');
  const parts = clean.trim().split(/\\s+/).filter(Boolean);
  if (parts.length === 0) return 'P';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const ProjectAvatar = ({ name = 'Project', workspaceId = '', size = 24 }) => {
  const initials = getProjectInitials(name);
  const bg = pickProjectColor(`${workspaceId}:${name || initials}`);
  return (
    <Avatar sx={{ width: size, height: size, fontSize: size * 0.45, bgcolor: bg }}>
      {initials}
    </Avatar>
  );
};

function TaskDetailPanel({
  taskPanelOpen,
  setTaskPanelOpen,
  selectedTaskForPanel,
  setSelectedTaskForPanel,
  selectedTaskComments,
  setSelectedTaskComments,
  taskCommentDraft,
  setTaskCommentDraft,
  taskSteps,
  setTaskSteps,
  availableStepGroups,
  setAvailableStepGroups,
  selectedStepGroupForAttach,
  setSelectedStepGroupForAttach,
  taskWorkLogs,
  setTaskWorkLogs,
  taskActivityLogs,
  setTaskActivityLogs,
  taskDetailTab,
  setTaskDetailTab,
  activityFrom,
  setActivityFrom,
  activityTo,
  setActivityTo,
  activityUserFilter,
  setActivityUserFilter,
  taskWorkLogDraft,
  setTaskWorkLogDraft,
  availableWorkLogApprovers,
  setAvailableWorkLogApprovers,
  linkedProjectIds,
  setLinkedProjectIds,
  getUserName,
  assignableUsers,
  visibleProjects,
  selectedWorkspace,
  userId,
  handleSaveTaskDetails,
  handleArchiveTask,
  handleAddTaskComment,
  handleCollaboratorsChange,
  loadTaskComments,
  handleToggleStep,
  handleAttachStepGroup,
  handleAddWorkLog,
  loadTask,
  loadTaskActivityLogs,
  enqueueSnackbar,
  taskDetailSaving,
  setTaskDetailSaving, // currently unused but kept for future
  fetchTasks,
  canLogWorkOnSelectedTask,
  loadTaskWorkLogs,
}) {
  const priorityOptions = [
    { value: 'low', label: 'Low', color: '#0ea5e9' },
    { value: 'medium', label: 'Medium', color: '#f59e0b' },
    { value: 'high', label: 'High', color: '#ef4444' },
  ];
  const statusOptions = [
    { value: 'to-do', label: 'To-Do', color: '#94a3b8' },
    { value: 'in-progress', label: 'In-Progress', color: '#0ea5e9' },
    { value: 'completed', label: 'Completed', color: '#22c55e' },
    { value: 'closed', label: 'Closed', color: '#334155' },
  ];
  // ---------- Helper: lock / completion states ----------
  const status = (selectedTaskForPanel?.status || '').toLowerCase();
  const completionState = (
    selectedTaskForPanel?.completion_state || ''
  ).toLowerCase(); // 'none' | 'pending' | 'approved' | 'rejected'

  const isPendingApproval =
    status === 'completed' && completionState === 'pending';
  const isClosed = status === 'closed';
  const isRejected =
    status === 'completed' && completionState === 'rejected';

  // When this is true, all “core” fields should be read-only
  const isLockedForEdits = isPendingApproval || isClosed;
  const [editingWorkLogId, setEditingWorkLogId] = React.useState(null);
  const friendlyDate = (val) => {
    if (!val) return 'Not set';
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return 'Not set';
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
    }).format(d);
  };
    

  // ---------- Common close / reset ----------
  const handleClosePanel = () => {
    setTaskPanelOpen(false);
    setSelectedTaskForPanel(null);
    setSelectedTaskComments([]);
    setTaskCommentDraft('');
    setTaskSteps([]);
    setAvailableStepGroups([]);
    setTaskWorkLogs([]);
    setTaskActivityLogs([]);
    setTaskDetailTab('comments');
    setTaskWorkLogDraft({
      date: new Date().toISOString().slice(0, 10),
      hours: '',
      minutes: '',
      notes: '',
      approver_user_id: '',
    });
    setAvailableWorkLogApprovers([]);
    setLinkedProjectIds([]);
    setEditingWorkLogId(null);

  };

  const handleUpdateWorkLog = async () => {
    if (!editingWorkLogId || !selectedTaskForPanel) return;

    try {
      await api.put(`/api/work-logs/${editingWorkLogId}`, {
        user_id: userId,
        hours: taskWorkLogDraft.hours || 0,
        minutes: taskWorkLogDraft.minutes || 0,
        notes: taskWorkLogDraft.notes || '',
        work_date: taskWorkLogDraft.date || null,
        approver_user_id: taskWorkLogDraft.approver_user_id || null,
      });

      await loadTaskWorkLogs(selectedTaskForPanel.id);

      // reset draft + editing mode
      setTaskWorkLogDraft({
        date: new Date().toISOString().slice(0, 10),
        hours: '',
        minutes: '',
        notes: '',
        approver_user_id: '',
      });
      setEditingWorkLogId(null);

      enqueueSnackbar?.('Work log updated', { variant: 'success' });
    } catch (err) {
      console.error('Failed to update work log:', err);
      enqueueSnackbar?.('Failed to update work log', { variant: 'error' });
    }
  };




  // ---------- Save + auto-close ----------
  const handleSaveClick = async () => {
    if (!selectedTaskForPanel) return;

    // Normalise tags: DB usually stores tags as an array
    const tagsValue = Array.isArray(selectedTaskForPanel.tags)
      ? selectedTaskForPanel.tags
      : (selectedTaskForPanel.tags || '')
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean);

    const changes = {
      title: selectedTaskForPanel.title,
      priority: selectedTaskForPanel.priority,
      status: selectedTaskForPanel.status,
      // project_id can be null or a number; backend will treat null as personal task
      project_id:
        selectedTaskForPanel.project_id === '' ||
        selectedTaskForPanel.project_id === null ||
        selectedTaskForPanel.project_id === undefined
          ? null
          : Number(selectedTaskForPanel.project_id),
      target_date: selectedTaskForPanel.target_date || null,
      due_date: selectedTaskForPanel.due_date || null,
      assigned_to:
        selectedTaskForPanel.assigned_to === '' ||
        selectedTaskForPanel.assigned_to === null ||
        selectedTaskForPanel.assigned_to === undefined
          ? null
          : Number(selectedTaskForPanel.assigned_to),
      description: selectedTaskForPanel.description || '',
      tags: tagsValue,
      // If later you add API for multi-project mapping:
      // linked_project_ids: linkedProjectIds,
    };

    try {
      const ok = await handleSaveTaskDetails(changes);
      if (ok) {
        enqueueSnackbar?.('Task updated', { variant: 'success' });
        handleClosePanel(); // ✅ auto close after successful save
      }
    } catch (err) {
      console.error('Error in handleSaveClick:', err);
      enqueueSnackbar?.('Failed to save task', { variant: 'error' });
    }
  };

  const renderLockChip = () => {
    if (!isLockedForEdits) return null;

    let label = 'Locked';
    if (isPendingApproval) label = 'Waiting for approval';
    else if (isClosed) label = 'Closed';
    else if (isRejected) label = 'Rejected – edit allowed'; // (but we’re not enforcing extra logic here)

    return (
      <Chip
        icon={<LockIcon fontSize="small" />}
        label={label}
        size="small"
        color={isClosed ? 'default' : 'warning'}
        variant={isClosed ? 'outlined' : 'filled'}
        sx={{ ml: 1 }}
      />
    );
  };

  return (
    <Drawer
      anchor="right"
      open={taskPanelOpen && Boolean(selectedTaskForPanel)}
      onClose={handleClosePanel}
      PaperProps={{
        sx: {
          top: 64, // keep below app bar
          width: 480,
          height: 'calc(100vh - 64px)',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#fff',
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {selectedTaskForPanel && (
          <>
            {/* Header */}
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 2,
                px: 2,
                pt: 2,
                pb: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                bgcolor: '#fff',
                boxShadow: '0 6px 12px rgba(0,0,0,0.06)',
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Task Edit
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {renderLockChip()}
                {!selectedTaskForPanel.archived && !isLockedForEdits && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={handleArchiveTask}
                    disabled={selectedTaskForPanel.status !== 'completed'}
                  >
                    Archive
                  </Button>
                )}
                {!isLockedForEdits && (
                  <Button
                    size="small"
                    variant="text"
                    color="error"
                    onClick={async () => {
                      if (window.confirm('Request deletion?')) {
                        await api.delete(
                          `/api/tasks/${selectedTaskForPanel.id}`,
                          { params: { user_id: userId } }
                        );
                        enqueueSnackbar?.(
                          'Deletion requested - awaiting approval'
                        );
                        fetchTasks?.();
                        handleClosePanel();
                      }
                    }}
                  >
                    Delete
                  </Button>
                )}
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleSaveClick}
                  disabled={taskDetailSaving || isLockedForEdits}
                >
                  {taskDetailSaving ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            </Box>

            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                px: 2,
                pb: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
              }}
            >

              {/* Title */}
              <TextField
                fullWidth
                label="Title"
                margin="dense"
                value={selectedTaskForPanel.title || ''}
                onChange={(e) =>
                  setSelectedTaskForPanel((t) => ({ ...t, title: e.target.value }))
                }
                error={(selectedTaskForPanel.title || '').trim().length < 3}
                helperText="Min 3 characters"
                disabled={isLockedForEdits}
              />

            {/* Project selector & chips */}
            <FormControl
              size="small"
              fullWidth
              sx={{ mb: 1, px: 2, mt: 1 }}
              disabled={isLockedForEdits}
            >
              <InputLabel id="panel-project-label">Project</InputLabel>
              <Select
                labelId="panel-project-label"
                label="Project"
                value={
                  selectedTaskForPanel.project_id != null
                    ? String(selectedTaskForPanel.project_id)
                    : ''
                }
                onChange={(e) =>
                  setSelectedTaskForPanel((t) => ({
                    ...t,
                    project_id: e.target.value || null,
                  }))
                }
                renderValue={(value) => {
                  if (!value) return 'Personal';
                  const proj = visibleProjects.find((p) => String(p.id) === String(value));
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <ProjectAvatar
                        name={proj?.name || 'Project'}
                        workspaceId={proj?.workplace_id || selectedWorkspace}
                        size={22}
                      />
                      <Typography variant="body2">{proj?.name || 'Project'}</Typography>
                    </Box>
                  );
                }}
              >
                <MenuItem value="">
                  <em>Personal (no project)</em>
                </MenuItem>
                {visibleProjects.map((p) => (
                  <MenuItem key={p.id} value={String(p.id)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <ProjectAvatar name={p.name} workspaceId={p.workplace_id || selectedWorkspace} size={22} />
                      <Typography variant="body2">{p.name}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', px: 2, mb: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  Priority
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  {priorityOptions.map((opt) => (
                    <Chip
                      key={opt.value}
                      label={opt.label}
                      size="small"
                      color={selectedTaskForPanel.priority === opt.value ? 'primary' : 'default'}
                      variant={selectedTaskForPanel.priority === opt.value ? 'filled' : 'outlined'}
                      onClick={() =>
                        !isLockedForEdits &&
                        setSelectedTaskForPanel((t) => ({ ...t, priority: opt.value }))
                      }
                      sx={
                        selectedTaskForPanel.priority === opt.value
                          ? { bgcolor: opt.color, color: '#fff', fontSize: 12, fontWeight: 700 }
                          : { fontSize: 12, fontWeight: 700 }
                      }
                    />
                  ))}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  Status
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  {statusOptions.map((opt) => (
                    <Chip
                      key={opt.value}
                      label={opt.label}
                      size="small"
                      color={selectedTaskForPanel.status === opt.value ? 'primary' : 'default'}
                      variant={selectedTaskForPanel.status === opt.value ? 'filled' : 'outlined'}
                      onClick={() =>
                        !isLockedForEdits &&
                        setSelectedTaskForPanel((t) => ({ ...t, status: opt.value }))
                      }
                      sx={
                        selectedTaskForPanel.status === opt.value
                          ? { bgcolor: opt.color, color: '#fff', fontSize: 12, fontWeight: 700 }
                          : { fontSize: 12, fontWeight: 700 }
                      }
                    />
                  ))}
                </Box>
              </Box>
            </Box>

            {/* Linked projects */}
            {selectedWorkspace && (
              <Box sx={{ mb: 1, px: 2 }}>
                <ProjectMultiTagSelector
                  projects={visibleProjects}
                  primaryProjectId={
                    selectedTaskForPanel.project_id != null
                      ? String(selectedTaskForPanel.project_id)
                      : ''
                  }
                  value={linkedProjectIds}
                  onChange={setLinkedProjectIds}
                  renderProjectOption={(p) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <ProjectAvatar
                        name={p.name}
                        workspaceId={p.workplace_id || selectedWorkspace}
                        size={20}
                      />
                      <Typography variant="body2">{p.name}</Typography>
                    </Box>
                  )}
                />
              </Box>
            )}

            {/* Dates */}
            <Box sx={{ display: 'flex', gap: 1, mb: 1, px: 2 }}>
              <TextField
                type="date"
                size="small"
                fullWidth
                label="Target date"
                InputLabelProps={{ shrink: true }}
                value={
                  selectedTaskForPanel.target_date
                    ? String(selectedTaskForPanel.target_date).slice(0, 10)
                    : ''
                }
                onChange={(e) =>
                  setSelectedTaskForPanel((t) => ({
                    ...t,
                    target_date: e.target.value,
                  }))
                }
                disabled={isLockedForEdits}
              />
              <TextField
                type="date"
                size="small"
                fullWidth
                label="Due date"
                InputLabelProps={{ shrink: true }}
                value={
                  selectedTaskForPanel.due_date
                    ? String(selectedTaskForPanel.due_date).slice(0, 10)
                    : ''
                }
                onChange={(e) =>
                  setSelectedTaskForPanel((t) => ({
                    ...t,
                    due_date: e.target.value,
                  }))
                }
                disabled={isLockedForEdits}
              />
            </Box>

            {/* Assignee */}
            <FormControl
              size="small"
              fullWidth
              sx={{ mb: 1, px: 2 }}
              disabled={isLockedForEdits}
            >
              <InputLabel id="panel-assignee-label">Assigned to</InputLabel>
              <Select
                labelId="panel-assignee-label"
                label="Assigned to"
                value={selectedTaskForPanel.assigned_to || ''}
                onChange={(e) =>
                  setSelectedTaskForPanel((t) => ({
                    ...t,
                    assigned_to: e.target.value || null,
                  }))
                }
                renderValue={(value) => {
                  if (!value) return <Typography variant="body2">None</Typography>;
                  const u = assignableUsers.find((usr) => Number(usr.id) === Number(value));
                  const displayUser = u || { username: `User ${value}` };
                  return <UserAvatar user={displayUser} size={28} />;
                }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {assignableUsers.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <UserAvatar user={u} size={24} />
                      <Typography variant="body2">{buildUserDisplay(u).firstName}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Collaborators */}
            <FormControl
              size="small"
              fullWidth
              sx={{ mb: 1, px: 2 }}
              disabled={isLockedForEdits}
            >
              <InputLabel id="panel-collaborators-label">
                Collaborators
              </InputLabel>
              <Select
                labelId="panel-collaborators-label"
                label="Collaborators"
                multiple
                value={
                  Array.isArray(selectedTaskForPanel.collaborators)
                    ? selectedTaskForPanel.collaborators.map((c) => c.user_id)
                    : []
                }
                onChange={(e) => handleCollaboratorsChange(e.target.value)}
                renderValue={(selected) => {
                  const ids = selected.map((id) => Number(id));
                  const users = assignableUsers.filter((u) => ids.includes(Number(u.id)));
                  if (!users.length) return 'None';
                  return (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {users.map((u) => (
                        <UserAvatar key={u.id} user={u} size={24} />
                      ))}
                    </Box>
                  );
                }}
              >
                {assignableUsers.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <UserAvatar user={u} size={24} />
                      <Typography variant="body2">{buildUserDisplay(u).firstName}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Tags */}
            <TextField
              label="Tags (comma separated)"
              fullWidth
              margin="dense"
              value={
                Array.isArray(selectedTaskForPanel.tags)
                  ? selectedTaskForPanel.tags.join(', ')
                  : selectedTaskForPanel.tags || ''
              }
              onChange={(e) =>
                setSelectedTaskForPanel((t) => ({
                  ...t,
                  tags: e.target.value,
                }))
              }
              disabled={isLockedForEdits}
              InputProps={{
                startAdornment: <LabelIcon sx={{ mr: 1, color: 'text.disabled' }} />,
              }}
            />

            {/* Meta info */}
            <Typography variant="caption" color="text.secondary">
              Created by {getUserName(selectedTaskForPanel.user_id)}{' '}
              {selectedTaskForPanel.created_at &&
                `on ${String(selectedTaskForPanel.created_at).slice(0, 10)}`}
            </Typography>

            {/* Description */}
            <TextField
              label="Description / notes"
              multiline
              minRows={4}
              fullWidth
              margin="dense"
              value={selectedTaskForPanel.description || ''}
              onChange={(e) =>
                setSelectedTaskForPanel((t) => ({
                  ...t,
                  description: e.target.value,
                }))
              }
              disabled={isLockedForEdits}
              sx={{ bgcolor: '#fff', borderRadius: 1 }}
            />

            {/* Attachments block */}
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 2,
                border: '1px dashed',
                borderColor: 'divider',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 0.5,
                }}
              >
                <Typography variant="subtitle2">Attachments</Typography>
                {!isLockedForEdits && (
                  <Button
                    variant="text"
                    size="small"
                    component="label"
                    sx={{ textTransform: 'none', fontWeight: 500 }}
                  >
                    Add attachment
                    <input
                      type="file"
                      hidden
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        if (!files.length) return;

                        const formData = new FormData();
                        files.forEach((file) => formData.append('files', file));
                        formData.append('user_id', userId);

                        fetch(`${API_BASE}/api/tasks/${selectedTaskForPanel.id}/attachments`, {
                          method: 'POST',
                          body: formData,
                        })
                          .then(async (res) => {
                            if (!res.ok) {
                              const text = await res.text().catch(() => '');
                              throw new Error(text || `Upload failed with status ${res.status}`);
                            }
                            return res.json();
                          })
                          .then((updatedTask) => {
                            setSelectedTaskForPanel((prev) =>
                              prev && prev.id === updatedTask.id ? updatedTask : prev
                            );
                            fetchTasks?.();
                            enqueueSnackbar?.('Attachment added', { variant: 'success' });
                          })
                          .catch((err) => {
                            console.error('Attachment upload error:', err);
                            enqueueSnackbar?.('Failed to upload attachment', { variant: 'error' });
                          });

                        // allow re-selecting same file
                        e.target.value = '';
                      }}
                    />
                  </Button>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                {(selectedTaskForPanel.attachments || []).length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    No attachments.
                  </Typography>
                )}

                {(selectedTaskForPanel.attachments || []).map((file, idx) => {
                  const isImage = isImageFile(file);
                  const fileUrl = `${API_BASE}/uploads/${file}`;
                  const ext = getFileExt(file).toUpperCase();

                  return (
                    <Tooltip key={idx} title={file} placement="top">
                      <Box
                        sx={{
                          position: 'relative',
                          width: 96,
                          height: 96,
                          borderRadius: 2,
                          overflow: 'hidden',
                          border: '1px solid',
                          borderColor: 'divider',
                          bgcolor: 'background.paper',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                        }}
                        component="a"
                        href={fileUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {isImage ? (
                          <img
                            src={fileUrl}
                            alt={file}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <Box sx={{ textAlign: 'center', p: 1 }}>
                            <AttachFileIcon fontSize="large" color="action" />
                            <Typography variant="caption" sx={{ display: 'block', fontSize: 10 }}>
                              {file.slice(0, 14)}
                            </Typography>
                          </Box>
                        )}

                        <Box
                          sx={{
                            position: 'absolute',
                            left: 6,
                            bottom: 6,
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 10,
                            bgcolor: 'rgba(0,0,0,0.55)',
                            color: '#fff',
                            fontSize: 10,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                          }}
                        >
                          {ext || 'FILE'}
                        </Box>

                        {!isLockedForEdits && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.preventDefault();
                              const url = `${API_BASE}/api/tasks/${
                                selectedTaskForPanel.id
                              }/attachment?filename=${encodeURIComponent(
                                file
                              )}&user_id=${encodeURIComponent(userId)}`;

                              fetch(url, { method: 'DELETE' })
                                .then(async (res) => {
                                  if (!res.ok) {
                                    const text = await res.text().catch(() => '');
                                    throw new Error(text || `Delete failed with status ${res.status}`);
                                  }
                                  await res.text().catch(() => '');
                                })
                                .then(() => {
                                  setSelectedTaskForPanel((prev) => {
                                    if (!prev) return prev;
                                    const current = Array.isArray(prev.attachments) ? prev.attachments : [];
                                    return {
                                      ...prev,
                                      attachments: current.filter((att) => att !== file),
                                    };
                                  });
                                  fetchTasks?.();
                                  enqueueSnackbar?.('Attachment removed', { variant: 'success' });
                                })
                                .catch((err) => {
                                  console.error('Attachment delete error:', err);
                                  enqueueSnackbar?.('Failed to delete attachment', { variant: 'error' });
                                });
                            }}
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              bgcolor: 'rgba(0,0,0,0.55)',
                              color: 'common.white',
                              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>

            {/* Checklist */}
            {(taskSteps.length > 0 || availableStepGroups.length > 0) && (
              <Box sx={{ mt: 2, mb: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                    gap: 1,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Checklist
                  </Typography>
                  {availableStepGroups.length > 0 && !isLockedForEdits && (
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <InputLabel id="step-group-select-label">
                        Attach template
                      </InputLabel>
                      <Select
                        labelId="step-group-select-label"
                        label="Attach template"
                        value={selectedStepGroupForAttach}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (!val) return;
                          handleAttachStepGroup(Number(val));
                          setSelectedStepGroupForAttach('');
                        }}
                      >
                        <MenuItem value="">
                          <em>Select...</em>
                        </MenuItem>
                        {availableStepGroups.map((sg) => (
                          <MenuItem key={sg.id} value={sg.id}>
                            {sg.name}{' '}
                            {sg.scope === 'workspace'
                              ? '(workspace)'
                              : '(project)'}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>

                {taskSteps.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    No checklist linked yet.
                  </Typography>
                )}

                {taskSteps.length > 0 && (
                  <List dense>
                    {taskSteps.map((s) => (
                      <ListItem key={s.step_item_id} sx={{ px: 0 }} disableGutters>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={Boolean(s.is_done)}
                              onChange={() =>
                                handleToggleStep(s.step_item_id, s.is_done)
                              }
                              disabled={isLockedForEdits}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2">
                                {s.seq_no ? `${s.seq_no}. ` : ''}
                                {s.title}
                                {s.is_mandatory && (
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color="error"
                                    sx={{ ml: 0.5 }}
                                  >
                                    (mandatory)
                                  </Typography>
                                )}
                              </Typography>
                              {s.description && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {s.description}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            )}

            <Divider sx={{ mb: 1 }} />

            {/* Tabs: Comments / Work Done / Activity */}
            <Tabs
              value={taskDetailTab}
              onChange={(_, val) => {
                setTaskDetailTab(val);
                if (val === 'activity' && selectedTaskForPanel) {
                  loadTaskActivityLogs(selectedTaskForPanel.id);
                }
              }}
              variant="fullWidth"
              sx={{ mb: 1 }}
            >
              <Tab label="Comments" value="comments" />
              <Tab label="Work Done" value="work" />
              <Tab label="Activity" value="activity" />
            </Tabs>

            {/* Comments tab */}
            {taskDetailTab === 'comments' && (
              <>
                <Box sx={{ mb: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Add a comment..."
                    value={taskCommentDraft}
                    onChange={(e) => setTaskCommentDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleAddTaskComment();
                      }
                    }}
                  />
                  <Box
                    sx={{
                      mt: 0.5,
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleAddTaskComment}
                      disabled={!taskCommentDraft.trim()}
                    >
                      Post
                    </Button>
                  </Box>
                </Box>

                <Box sx={{ maxHeight: 200, overflowY: 'auto', mt: 1 }}>
                  {selectedTaskComments.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      No comments yet.
                    </Typography>
                  ) : (
                    selectedTaskComments.map((c) => (
                      <Box key={c.id} sx={{ mb: 1.5 }}>
                        <Typography variant="body2">{c.content}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          by User {c.user_id}{' '}
                          {c.created_at &&
                            `on ${String(c.created_at).slice(0, 10)}`}
                        </Typography>

                        {/* Replies */}
                        {Array.isArray(c.replies) &&
                          c.replies.map((r) => (
                            <Box key={r.id} sx={{ ml: 2, mt: 1 }}>
                              <Typography variant="body2">
                                {r.content}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                by User {r.user_id}{' '}
                                {r.created_at &&
                                  `on ${String(r.created_at).slice(0, 10)}`}
                              </Typography>
                            </Box>
                          ))}

                        {/* Quick reply */}
                        <Button
                          size="small"
                          onClick={async () => {
                            const reply = prompt('Reply to comment:');
                            if (reply?.trim()) {
                              await api.post('/api/comments', {
                                task_id: selectedTaskForPanel.id,
                                user_id: userId,
                                content: reply.trim(),
                                parent_id: c.id,
                              });
                              loadTaskComments(selectedTaskForPanel.id);
                            }
                          }}
                        >
                          Reply
                        </Button>
                      </Box>
                    ))
                  )}
                </Box>
              </>
            )}

            {/* Work Done tab */}
            {taskDetailTab === 'work' && (
              <>
                {canLogWorkOnSelectedTask && (
                  <Box
                    sx={{
                      mb: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                    }}
                  >
                   <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600 }}
                    >
                      {editingWorkLogId ? 'Edit work done' : 'Add work done'}
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      <TextField
                        type="date"
                        size="small"
                        label="Date"
                        InputLabelProps={{ shrink: true }}
                        value={taskWorkLogDraft.date}
                        onChange={(e) =>
                          setTaskWorkLogDraft((prev) => ({
                            ...prev,
                            date: e.target.value,
                          }))
                        }
                      />
                      <TextField
                        size="small"
                        label="Hours"
                        type="number"
                        inputProps={{ min: 0 }}
                        value={taskWorkLogDraft.hours}
                        onChange={(e) =>
                          setTaskWorkLogDraft((prev) => ({
                            ...prev,
                            hours: e.target.value,
                          }))
                        }
                      />
                      <TextField
                        size="small"
                        label="Minutes"
                        type="number"
                        inputProps={{ min: 0, max: 59 }}
                        value={taskWorkLogDraft.minutes}
                        onChange={(e) =>
                          setTaskWorkLogDraft((prev) => ({
                            ...prev,
                            minutes: e.target.value,
                          }))
                        }
                      />

                      {/* Approver selection */}
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id="worklog-approver-label">
                          Approver
                        </InputLabel>
                        <Select
                          labelId="worklog-approver-label"
                          label="Approver"
                          value={taskWorkLogDraft.approver_user_id || ''}
                          onChange={(e) =>
                            setTaskWorkLogDraft((prev) => ({
                              ...prev,
                              approver_user_id: e.target.value || '',
                            }))
                          }
                        >
                          {availableWorkLogApprovers.length === 0 && (
                            <MenuItem value="">
                              <em>No approver rules (optional)</em>
                            </MenuItem>
                          )}
                          {availableWorkLogApprovers.length > 0 && (
                            <MenuItem value="" key="placeholder">
                              <em>
                                {availableWorkLogApprovers.length === 1
                                  ? 'Auto-selected'
                                  : 'Select approver'}
                              </em>
                            </MenuItem>
                          )}
                          {availableWorkLogApprovers.map((u) => {
                            const id =
                              u.user_id ??
                              u.approver_user_id ??
                              u.id ??
                              u.approver_id;
                            const label =
                              u.username ||
                              u.name ||
                              u.approver_username ||
                              u.email ||
                              `User ${id}`;
                            return (
                              <MenuItem key={String(id)} value={String(id)}>
                                {label}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Box>

                    <TextField
                      size="small"
                      label="Notes"
                      multiline
                      minRows={2}
                      value={taskWorkLogDraft.notes}
                      onChange={(e) =>
                        setTaskWorkLogDraft((prev) => ({
                          ...prev,
                          notes: e.target.value,
                        }))
                      }
                    />

                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => {
                          if (editingWorkLogId) {
                            handleUpdateWorkLog();
                          } else {
                            handleAddWorkLog();
                          }
                        }}
                        disabled={
                          !taskWorkLogDraft.hours &&
                          !taskWorkLogDraft.minutes
                        }
                      >
                        {editingWorkLogId ? 'Update work done' : 'Submit work done'}
                      </Button>

                    </Box>
                  </Box>
                )}

                <Typography variant="body2">
                  Total Logged:{' '}
                  {taskWorkLogs
                    .reduce(
                      (acc, w) =>
                        acc +
                        (w.hours || 0) +
                        (w.minutes || 0) / 60,
                      0
                    )
                    .toFixed(1)}{' '}
                  hours
                </Typography>

                <Box sx={{ maxHeight: 220, overflowY: 'auto', mt: 1 }}>
                  {taskWorkLogs.length === 0 ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      No work logged yet.
                    </Typography>
                  ) : (
                    taskWorkLogs.map((w) => {
                      const dateStr = w.work_date
                        ? String(w.work_date).slice(0, 10)
                        : '';
                      const totalMinutes =
                        (w.hours != null ? Number(w.hours) : 0) * 60 +
                        (w.minutes != null ? Number(w.minutes) : 0);
                      const h = Math.floor(totalMinutes / 60);
                      const m = totalMinutes % 60;
                      const hm = `${h}h ${m}m`;
                      const logStatus =
                        w.status ||
                        (w.approved ? 'approved' : 'pending');

                      return (
                        <Box key={w.id} sx={{ mb: 1.5 }}>
                          <Typography variant="body2">
                            {dateStr} – {hm}{' '}
                            <Typography
                              component="span"
                              variant="caption"
                              color={
                                logStatus === 'approved'
                                  ? 'success.main'
                                  : 'warning.main'
                              }
                              sx={{ ml: 1 }}
                            >
                              {logStatus}
                            </Typography>
                          </Typography>
                          {w.notes && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {w.notes}
                            </Typography>
                          )}
                          {logStatus !== 'approved' &&
                            Number(w.user_id) === Number(userId) && (
                              <Button
                                size="small"
                                onClick={() => {
                                  // Load this work log into the form
                                  setTaskWorkLogDraft({
                                    date: w.work_date ? String(w.work_date).slice(0, 10) : '',
                                    hours: w.hours ?? '',
                                    minutes: w.minutes ?? '',
                                    notes: w.notes || '',
                                    approver_user_id: w.approver_user_id || '',
                                  });
                                  // Mark which log we are editing
                                  setEditingWorkLogId(w.id);
                                }}
                              >
                                Edit
                              </Button>
                            )}

                        </Box>
                      );
                    })
                  )}
                </Box>
              </>
            )}

            {/* Activity tab */}
            {taskDetailTab === 'activity' && (
              <>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    type="date"
                    size="small"
                    label="From"
                    value={activityFrom}
                    onChange={(e) => setActivityFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    type="date"
                    size="small"
                    label="To"
                    value={activityTo}
                    onChange={(e) => setActivityTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>User</InputLabel>
                    <Select
                      value={activityUserFilter}
                      onChange={(e) =>
                        setActivityUserFilter(e.target.value)
                      }
                    >
                      <MenuItem value="">All</MenuItem>
                      {assignableUsers.map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                          {u.username}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() =>
                      loadTaskActivityLogs(selectedTaskForPanel.id)
                    }
                  >
                    Apply Filter
                  </Button>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={async () => {
                      const res = await api.get('/api/activity-logs', {
                        params: {
                          task_id: selectedTaskForPanel.id,
                          format: 'csv',
                        },
                      });
                      const blob = new Blob([res.data], {
                        type: 'text/csv',
                      });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'task_audit.csv';
                      a.click();
                    }}
                  >
                    Export CSV
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={async () => {
                      const res = await api.get('/api/activity-logs', {
                        params: {
                          task_id: selectedTaskForPanel.id,
                          format: 'pdf',
                        },
                        responseType: 'blob',
                      });
                      const url = URL.createObjectURL(res.data);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'task_audit.pdf';
                      a.click();
                    }}
                  >
                    Export PDF
                  </Button>
                </Box>

                <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
                  {taskActivityLogs.length === 0 ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      No activity yet.
                    </Typography>
                  ) : (
                    taskActivityLogs.map((log) => (
                      <Box key={log.id} sx={{ mb: 1.5 }}>
                        <Typography variant="body2">
                          {log.message}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          {log.created_at} by User {log.user_id}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>
              </>
            )}
          </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
}

export default TaskDetailPanel;
